import type { HeightFrame, ProfilePoint } from "../types";

export interface StabilityBand {
  upperBound: number;
  color: string;
  label: string;
}

// TJ Olney's sampled windgram palette and the same C / 1000 ft boundaries
// used by the Python share-card renderer.
export const STABILITY_BANDS: StabilityBand[] = [
  { upperBound: -3, color: "#eb4f44", label: "Absolutely unstable" },
  {
    upperBound: -2.5,
    color: "#f2a13b",
    label: "Conditionally unstable — strong"
  },
  {
    upperBound: -2,
    color: "#f6c5fb",
    label: "Conditionally unstable — good"
  },
  {
    upperBound: -1.5,
    color: "#cbbffa",
    label: "Conditionally unstable — workable"
  },
  {
    upperBound: -1.2,
    color: "#fdf3ed",
    label: "Conditionally unstable — marginal"
  },
  { upperBound: 0, color: "#7c7ddf", label: "Absolutely stable" },
  { upperBound: 0.5, color: "#d1d2d1", label: "Inverted" },
  {
    upperBound: Number.POSITIVE_INFINITY,
    color: "#9c999c",
    label: "Inverted — strong"
  }
];

export function heightInFrame(
  point: Pick<ProfilePoint, HeightFrame>,
  frame: HeightFrame
): number | null {
  const value = point[frame];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function stabilityBand(rateCPer1000Ft: number): StabilityBand {
  return (
    STABILITY_BANDS.find((band) => rateCPer1000Ft <= band.upperBound) ??
    STABILITY_BANDS[STABILITY_BANDS.length - 1]
  );
}

export interface StabilityLayer {
  lowerFt: number;
  upperFt: number;
  rateCPer1000Ft: number;
  color: string;
  label: string;
}

export function stabilityLayers(
  profile: ProfilePoint[],
  frame: HeightFrame
): StabilityLayer[] {
  const points = profile
    // Match the static renderer: the colored field is made from atmospheric
    // pressure-level lapse rates. Screen-level T2m drives the parcel, but is
    // not spliced into the free-atmosphere stability field.
    .filter((point) => point.kind === "pressure_level")
    .map((point) => ({ point, height: heightInFrame(point, frame) }))
    .filter(
      (item): item is { point: ProfilePoint; height: number } =>
        item.height !== null
    )
    .sort((a, b) => a.height - b.height);

  const layers: StabilityLayer[] = [];
  for (let index = 0; index < points.length - 1; index += 1) {
    const lower = points[index];
    const upper = points[index + 1];
    const spanThousands = (upper.height - lower.height) / 1000;
    if (spanThousands <= 0) continue;
    const rate =
      (upper.point.temperature_c - lower.point.temperature_c) / spanThousands;
    const band = stabilityBand(rate);
    layers.push({
      lowerFt: lower.height,
      upperFt: upper.height,
      rateCPer1000Ft: rate,
      color: band.color,
      label: band.label
    });
  }
  return layers;
}

export function heightAtTemperature(
  profile: ProfilePoint[],
  targetC: number,
  frame: HeightFrame
): number | null {
  const points = profile
    .filter((point) => point.kind === "pressure_level")
    .map((point) => ({ point, height: heightInFrame(point, frame) }))
    .filter(
      (item): item is { point: ProfilePoint; height: number } =>
        item.height !== null
    )
    .sort((a, b) => a.height - b.height);

  for (let index = 0; index < points.length - 1; index += 1) {
    const lower = points[index];
    const upper = points[index + 1];
    const lowDelta = lower.point.temperature_c - targetC;
    const highDelta = upper.point.temperature_c - targetC;
    if (lowDelta * highDelta > 0) continue;
    const temperatureSpan =
      lower.point.temperature_c - upper.point.temperature_c;
    if (Math.abs(temperatureSpan) < 1e-9) return lower.height;
    const fraction = lowDelta / temperatureSpan;
    return lower.height + fraction * (upper.height - lower.height);
  }
  return null;
}

export function compass(direction: number | null): string {
  if (direction === null || !Number.isFinite(direction)) return "—";
  const labels = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return labels[Math.round((((direction % 360) + 360) % 360) / 45) % 8];
}

export function windColor(speedKt: number, inFlightLayer: boolean): string {
  if (!inFlightLayer) return "#566473";
  if (speedKt < 4) return "#8fa0ab";
  if (speedKt <= 12) return "#177047";
  if (speedKt <= 15) return "#14588c";
  return "#b31320";
}

export function niceCeiling(maximum: number): number {
  if (!Number.isFinite(maximum) || maximum <= 0) return 10_000;
  return Math.ceil(maximum / 1000) * 1000;
}
